import React from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';

const TermsOfService = () => {
  const lastUpdated = 'January 27, 2026';

  return (
    <Box>
      {/* Page Header */}
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <GavelIcon color="primary" sx={{ mr: 1, fontSize: '2rem' }} />
          Terms of Service
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Last updated: {lastUpdated}
        </Typography>
      </Box>

      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        {/* Introduction */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Agreement to Terms
        </Typography>
        <Typography variant="body1" paragraph>
          By accessing or using FootyManager, operated by QuailGame ("we," "our," or "us"), you agree 
          to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Description of Service */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Description of Service
        </Typography>
        <Typography variant="body1" paragraph>
          FootyManager is a web application for managing soccer team attendance, events, leagues, and RSVP tracking. 
          The service allows team managers and league administrators to organize teams, schedule games, 
          track player attendance, and manage team communications.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* User Accounts */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          User Accounts
        </Typography>
        <Typography variant="body1" paragraph>
          To use certain features of FootyManager, you must create an account. You are responsible for:
        </Typography>
        <Typography variant="body1" component="div" paragraph>
          <ul>
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Providing accurate and current information</li>
            <li>Notifying us immediately of any unauthorized use of your account</li>
          </ul>
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Acceptable Use */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Acceptable Use
        </Typography>
        <Typography variant="body1" paragraph>
          You agree not to use FootyManager to:
        </Typography>
        <Typography variant="body1" component="div" paragraph>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe on the rights of others</li>
            <li>Upload or transmit harmful content, including viruses or malicious code</li>
            <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
            <li>Use the service for any purpose other than team and league management</li>
            <li>Harass, abuse, or harm other users</li>
            <li>Collect user information without consent</li>
          </ul>
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* User Content */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          User Content
        </Typography>
        <Typography variant="body1" paragraph>
          You retain ownership of content you submit to FootyManager (team names, player information, etc.). 
          By submitting content, you grant us a license to use, store, and display that content as necessary 
          to provide our services.
        </Typography>
        <Typography variant="body1" paragraph>
          You are responsible for ensuring you have the right to submit any content and that it does not 
          violate the rights of any third party.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Intellectual Property */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Intellectual Property
        </Typography>
        <Typography variant="body1" paragraph>
          The FootyManager application, including its design, features, and content (excluding user-submitted content), 
          is owned by QuailGame and protected by intellectual property laws. You may not copy, modify, distribute, 
          or create derivative works without our written permission.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Service Availability */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Service Availability
        </Typography>
        <Typography variant="body1" paragraph>
          We strive to keep FootyManager available at all times but do not guarantee uninterrupted access. 
          We may modify, suspend, or discontinue the service at any time without notice. We are not liable 
          for any interruption or loss of service.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Disclaimer of Warranties */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Disclaimer of Warranties
        </Typography>
        <Typography variant="body1" paragraph>
          FootyManager is provided "as is" and "as available" without warranties of any kind, either express 
          or implied. We do not warrant that the service will be error-free, secure, or meet your specific requirements.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Limitation of Liability */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Limitation of Liability
        </Typography>
        <Typography variant="body1" paragraph>
          To the maximum extent permitted by law, QuailGame shall not be liable for any indirect, incidental, 
          special, consequential, or punitive damages arising from your use of FootyManager. Our total liability 
          shall not exceed the amount you paid us (if any) in the 12 months preceding the claim.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Termination */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Termination
        </Typography>
        <Typography variant="body1" paragraph>
          We may terminate or suspend your account at any time for violation of these terms or for any other 
          reason at our discretion. You may terminate your account by contacting us at info@quailgame.com.
        </Typography>
        <Typography variant="body1" paragraph>
          Upon termination, your right to use the service ceases immediately. Provisions that by their nature 
          should survive termination will remain in effect.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Governing Law */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Governing Law
        </Typography>
        <Typography variant="body1" paragraph>
          These Terms of Service are governed by and construed in accordance with applicable laws. 
          Any disputes arising from these terms or your use of FootyManager shall be resolved through 
          appropriate legal channels.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Changes to Terms */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Changes to Terms
        </Typography>
        <Typography variant="body1" paragraph>
          We may update these Terms of Service from time to time. We will notify users of material changes 
          by posting the updated terms on this page and updating the "Last updated" date. Continued use of 
          the service after changes constitutes acceptance of the new terms.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Contact Us */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have questions about these Terms of Service, please contact us at:
        </Typography>
        <Typography variant="body1">
          Email: <a href="mailto:info@quailgame.com">info@quailgame.com</a>
        </Typography>
      </Paper>
    </Box>
  );
};

export default TermsOfService;
